import React from 'react';
import { useAccounts } from '@v2/hooks';
import type { BillingAuditQuery } from '@v2/types';

interface BillingAuditFiltersProps {
  filters: BillingAuditQuery;
  onChange: (next: BillingAuditQuery) => void;
}

const labelClass = 'tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block';
const inputClass =
  'tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-full focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-400';

/** Convert a UTC ISO string (e.g. "2026-04-01T00:00:00Z") to a date-input value "YYYY-MM-DD". */
function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  return iso.substring(0, 10);
}

/** Convert a date-input value "YYYY-MM-DD" to a UTC ISO string at start-of-day. */
function dateInputToSince(value: string): string {
  return value ? `${value}T00:00:00Z` : '';
}

/** Convert a date-input value "YYYY-MM-DD" to a UTC ISO string at end-of-day (inclusive). */
function dateInputToUntil(value: string): string {
  return value ? `${value}T23:59:59Z` : '';
}

const BillingAuditFilters: React.FC<BillingAuditFiltersProps> = ({ filters, onChange }) => {
  const set = (patch: Partial<BillingAuditQuery>) => onChange({ ...filters, ...patch, page: 1 });
  const { accounts, loading: accountsLoading } = useAccounts();

  return (
    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-p-4 tw-bg-gray-50 tw-rounded tw-border tw-border-gray-200 tw-mb-4">
      <div className="tw-min-w-[160px]">
        <label className={labelClass} htmlFor="ba-since">
          From (UTC date)
        </label>
        <input
          id="ba-since"
          type="date"
          className={inputClass}
          value={isoToDateInput(filters.since)}
          onChange={e =>
            set({ since: e.target.value ? dateInputToSince(e.target.value) : undefined })
          }
          aria-label="Filter from UTC date"
        />
      </div>

      <div className="tw-min-w-[160px]">
        <label className={labelClass} htmlFor="ba-until">
          To (UTC date, inclusive)
        </label>
        <input
          id="ba-until"
          type="date"
          className={inputClass}
          value={isoToDateInput(filters.until)}
          onChange={e =>
            set({ until: e.target.value ? dateInputToUntil(e.target.value) : undefined })
          }
          aria-label="Filter to UTC date"
        />
      </div>

      <div className="tw-min-w-[200px]">
        <label className={labelClass} htmlFor="ba-operator">
          Operator
        </label>
        <select
          id="ba-operator"
          className={inputClass}
          value={filters.operatorAccountId ?? ''}
          onChange={e => set({ operatorAccountId: e.target.value || undefined })}
          disabled={accountsLoading}
          aria-label="Filter by operator">
          <option value="">All operators</option>
          {accounts.map(a => (
            <option key={a.id} value={a.id}>
              {a.firstName} {a.lastName} ({a.email})
            </option>
          ))}
        </select>
      </div>

      <div className="tw-min-w-[200px]">
        <label className={labelClass} htmlFor="ba-practice">
          Practice ID (GUID)
        </label>
        <input
          id="ba-practice"
          type="text"
          className={inputClass}
          placeholder="xxxxxxxx-xxxx-..."
          value={filters.practiceId ?? ''}
          onChange={e => set({ practiceId: e.target.value || undefined })}
          aria-label="Filter by practice ID"
        />
      </div>

      <div className="tw-min-w-[200px]">
        <label className={labelClass} htmlFor="ba-location">
          Location ID (GUID)
        </label>
        <input
          id="ba-location"
          type="text"
          className={inputClass}
          placeholder="xxxxxxxx-xxxx-..."
          value={filters.practiceLocationId ?? ''}
          onChange={e => set({ practiceLocationId: e.target.value || undefined })}
          aria-label="Filter by location ID"
        />
      </div>
    </div>
  );
};

export default BillingAuditFilters;
